import type { ChangelogVersion, ChangeEntry, ChangeType } from './types';

/**
 * Parser do CHANGELOG.md → ChangelogVersion[] (paridade com o Hub).
 *
 * Fonte única de verdade = CHANGELOG.md (servido por GET /api/changelog).
 * O texto das entradas é escrito em prosa amigável ao usuário final
 * (padrão Hub: "- **Módulo**: explicação amigável. [ISSUE]"); o parser
 * só remove a issue key para exibição e a expõe separada em `issue`.
 */

// Issue keys suportadas (prefixos dos produtos)
const ISSUE_PATTERN = /\[((?:TMF9|TH|TMF|DAIS|HC|FIN|CE|SUP|S2|INFRA)-\d+)\]/;
const PR_SUFFIX = /\s*\(#\d+\)\s*$/;
// Prefixo conventional-commit no começo da entrada (fix(scope): / feat: ...)
const CONV_PREFIX = /^(feat|fix|refactor|docs|chore|perf|test|style|build|ci)(\([^)]*\))?:\s*/i;

// Seção do CHANGELOG (### Header) → tipo de mudança do tmf9
const SECTION_TO_TYPE: Record<string, ChangeType> = {
  Adicionado: 'feat',
  Corrigido: 'fix',
  Alterado: 'refactor',
  Mudado: 'refactor',
  Documentação: 'docs',
  Docs: 'docs',
  Infra: 'chore',
  Removido: 'chore',
  Segurança: 'fix',
};

function extractIssue(text: string): string | undefined {
  const m = text.match(ISSUE_PATTERN);
  return m ? m[1] : undefined;
}

/** Remove issue key, sufixo de PR e prefixo conventional-commit. */
function cleanText(text: string): string {
  return text
    .replace(ISSUE_PATTERN, '')
    .replace(PR_SUFFIX, '')
    .replace(CONV_PREFIX, '')
    .replace(/\*\*/g, '')
    .trim()
    .replace(/\s+$/, '');
}

/** Extrai o "módulo" de uma entrada no formato "**Módulo**: descrição". */
function extractModule(text: string): string | undefined {
  const m = text.match(/^\*\*([^*]+)\*\*\s*[:\-—]/);
  return m ? m[1].trim() : undefined;
}

export function parseChangelog(md: string): ChangelogVersion[] {
  const versions: ChangelogVersion[] = [];
  const lines = md.split('\n');

  let current: ChangelogVersion | null = null;
  let currentType: ChangeType | null = null;

  for (const rawLine of lines) {
    const line = rawLine.replace(/\r$/, '');

    // ## [v9.77.0] - 2026-06-09 [14:30]
    const verMatch = line.match(/^##\s+\[([^\]]+)\]\s*-?\s*(.*)$/);
    if (verMatch) {
      const label = verMatch[1].trim();
      if (label.toLowerCase() === 'unreleased') {
        current = null;
        currentType = null;
        continue;
      }
      const rest = (verMatch[2] || '').trim();
      const [datePart, timePart] = rest.split(/\s+/);
      current = {
        version: label.replace(/^v/, ''),
        date: (datePart || '').trim(),
        time: timePart ? timePart.replace('h', '') : undefined,
        summary: '',
        changes: [],
      };
      versions.push(current);
      currentType = null;
      continue;
    }

    if (!current) continue;

    // ### Adicionado / Corrigido / ...
    const secMatch = line.match(/^###\s+(.+)$/);
    if (secMatch) {
      const header = secMatch[1].trim();
      currentType = SECTION_TO_TYPE[header] ?? 'chore';
      continue;
    }

    // - item de mudança (suporta multilinha simples ignorando continuações indentadas)
    const itemMatch = line.match(/^[-*]\s+(.+)$/);
    if (itemMatch && currentType) {
      const raw = itemMatch[1];
      const description = cleanText(raw);
      if (!description) continue;
      const entry: ChangeEntry = {
        type: currentType,
        module: extractModule(raw),
        description,
        // Entradas do CHANGELOG.md já são user-facing → servem ao popup direto.
        user_description: description,
        issue: extractIssue(raw),
      };
      current.changes.push(entry);
    }
  }

  // Summary automático por versão (contagem feat/fix)
  for (const v of versions) {
    const feats = v.changes.filter((c) => c.type === 'feat').length;
    const fixes = v.changes.filter((c) => c.type === 'fix').length;
    const parts: string[] = [];
    if (feats) parts.push(`${feats} novidade${feats > 1 ? 's' : ''}`);
    if (fixes) parts.push(`${fixes} correção${fixes > 1 ? 'ões' : ''}`);
    v.summary = parts.join(', ') || 'Atualizações e melhorias';
    v.user_summary = v.summary;
  }

  return versions;
}
